import React, { useEffect, useState } from 'react';
import { Row, Col, Form, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { Container, Modal } from '../layout';
import styles from './Canvasses.module.css';
import SimpleTable from '../layout/table/SimpleTable';
import { SearchBar } from '../layout/inputs/SearchBar';
import { Select } from '../layout/inputs/Select';
import { fetchCanvasses, searchCanvasses, fetchCanvassOnboardingInterview, duplicateCanvass, deleteCanvass } from '../../utils/canvasses';
import AugAlert from '../layout/alert/AugAlert';
import { Button as AugButton } from '../layout/buttons/Button';
import { useNavigate } from 'react-router-dom';
import Icon from '../layout/icons/Icons';
import { pdf } from '@react-pdf/renderer';
import ChatHistoryPDFDocumentCanvass from '../ChatHistoryPDFDocumentCanvass';
import { startAutomation } from '../../utils/automate';
import { logger } from '../../utils/logger';
import { useUserRoles } from '../../utils/useUserRoles';

const CanvassesTable = () => {
    const navigate = useNavigate();
    const { isAdmin } = useUserRoles();

    const [showModal, setShowModal] = useState(false);
    const [selectedCanvass, setSelectedCanvass] = useState(null);
    const [threadLoading, setThreadLoading] = useState(false);
    const [threadError, setThreadError] = useState('');
    const [threadMessages, setThreadMessages] = useState([]);

    const [automationLoading, setAutomationLoading] = useState(false);
    const [automationError, setAutomationError] = useState('');
    const [showAutomationConfirm, setShowAutomationConfirm] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [automationInterviewCount, setAutomationInterviewCount] = useState(1);
    const [customPromptInjection, setCustomPromptInjection] = useState('');
    const [duplicationLoading, setDuplicationLoading] = useState(false);
    const [duplicationError, setDuplicationError] = useState('');
    const [deletionLoading, setDeletionLoading] = useState(false);
    const [deletionError, setDeletionError] = useState('');

    const [searchTerm, setSearchTerm] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('');
    const [canvasses, setCanvasses] = useState([]);
    const [canvassesCount, setCanvassesCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);

    useEffect(() => {
        let isCancelled = false;
        const controller = new AbortController();
        const signal = controller.signal;

        const load = async () => {
            try {
                setLoading(true);
                setError('');
                let data;
                if (searchTerm && searchTerm.trim().length > 0) {
                    data = await searchCanvasses({ search: searchTerm.trim(), page, pageSize, returnFull: true, selectedStatus });
                } else {
                    data = await fetchCanvasses({ page, pageSize, returnFull: true, selectedStatus });
                }
                if (!isCancelled) {
                    setCanvasses(Array.isArray(data.canvasses) ? data.canvasses : []);
                    setCanvassesCount(data.totalCount);
                }
            } catch (e) {
                if (!isCancelled) setError(e?.message || 'Failed to load canvasses');
            } finally {
                if (!isCancelled) setLoading(false);
            }
        };

        // debounce
        const t = setTimeout(load, searchTerm ? 350 : 0);
        return () => {
            isCancelled = true;
            controller.abort();
            clearTimeout(t);
        };
    }, [page, pageSize, searchTerm, selectedStatus]);

    // Sync pageSize with the per-page select (dateRange here acts as page size selector)
    useEffect(() => {
        const n = parseInt(pageSize, 10);
        if (!Number.isNaN(n)) setPageSize(n);
    }, [pageSize]);

    // Reset to first page when search term changes
    useEffect(() => {
        setPage(1);
    }, [searchTerm]);

    const hasNext = canvasses.length === pageSize; // heuristic without totalCount

    const renderCell = (val) => {
        if (val == null) return '—';
        if (typeof val === 'string' || typeof val === 'number' || typeof val === 'boolean') return String(val);
        // Avoid rendering objects/arrays directly
        try {
            return JSON.stringify(val);
        } catch {
            return String(val);
        }
    };

    // Render status using CSS module classes: outer pill + inner status modifier class
    const renderStatus = (status) => {
        const s = String(status || '').toLowerCase();
        let label = '—';
        // Map status to a CSS class key defined in Canvasses.module.css
        let classKey = '';
        
        switch (s) {
            case 'drafting':
            case 'draft':
                label = 'Draft';
                classKey = 'draft';
                break;
            case 'in_progress':
                label = 'Draft';
                classKey = 'draft';
                break;
            case 'approved':
                label = 'Pending Activation';
                classKey = 'approved';
                break;
            case 'active':
                label = 'Live';
                classKey = 'live';
                break;
            case 'complete':
                label = 'Complete';
                classKey = 'completed';
                break;
            default:
                label = s ? s.charAt(0).toUpperCase() + s.slice(1) : '—';
                classKey = '';
        }
        return (
            <div className={styles.statusSlot}>
                <div className={styles.statusContainer + ' ' + styles.statusBadge + ' ' + styles[classKey]}>
                    <div className={classKey ? styles[classKey] : undefined}>{label}</div>
                </div>
            </div>
        );
    };

    const onPageChange = (nextPage) => {
        if (nextPage < 1) return;
        if (loading) return;
        setPage(nextPage);
    };
    const onPrev = () => onPageChange(page - 1);
    const onNext = () => onPageChange(page + 1);

    // Export the onboarding interview thread for the selected canvass as a PDF
    const exportCanvassConversationToPDF = async (includeMessages = false) => {
        if (!selectedCanvass) return;
        try {
            const conversation = {
                // Include all canvass fields so the PDF can render details like objective, audience, etc.
                ...selectedCanvass,
                // Ensure identifiers are present and consistent
                conversationId: `${selectedCanvass.canvassId}`,
                id: `${selectedCanvass.canvassId}`,
                // Derive timing and message stats from thread when available
                startTime: selectedCanvass.timestamp || (Array.isArray(threadMessages) && threadMessages[0]?.timestamp) || new Date().toISOString(),
                endTime: (Array.isArray(threadMessages) && threadMessages[threadMessages.length - 1]?.timestamp) || selectedCanvass.timestamp || new Date().toISOString(),
                userMessageCount: Array.isArray(threadMessages) ? threadMessages.filter(m => m.role === 'user').length : 0,
                messageCount: Array.isArray(threadMessages) ? threadMessages.length : 0,
                messages: Array.isArray(threadMessages) ? threadMessages : [],
                includeMessages,
            };

            const doc = <ChatHistoryPDFDocumentCanvass selectedConversation={conversation} />;
            const asPdf = pdf(doc);
            const blob = await asPdf.toBlob();

            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `August-Says-Canvass-${selectedCanvass.canvassId}-${new Date().toISOString().split('T')[0]}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (error) {
            logger.error('Error generating canvass PDF:', error);
        }
    };

    const renderRows = () => {
        if (!Array.isArray(canvasses) || canvasses.length === 0) {
            return (
                <tr>
                    <td colSpan={4} className="text-center text-muted">No canvasses found</td>
                </tr>
            );
        }
        const openCanvassModal = async (c) => {
            setSelectedCanvass(c);
            setShowModal(true);
            setThreadLoading(true);
            setThreadError('');
            setThreadMessages([]);
            try {
                const result = await fetchCanvassOnboardingInterview({ canvassId: c.canvassId });
                // API may return an array of interviewMessage docs; take first then read messages
                const doc = Array.isArray(result) ? result[0] : result;
                const msgs = doc?.messages || [];
                setThreadMessages(msgs);
            } catch (e) {
                setThreadError(e?.message || 'Failed to load conversation thread');
            } finally {
                setThreadLoading(false);
            }
        };

        return canvasses.map((c, idx) => (
            <tr
                key={c.id || c.canvassId || idx}
                style={{ cursor: c.canvassId ? 'pointer' : 'default' }}
                onClick={() => {
                    if (c.canvassId) {
                        navigate(`/canvass/${encodeURIComponent(c.canvassId)}`);
                    }
                }}
            >
                <td>{c.canvassName || c.canvassId || 'Untitled'}</td>
                <td>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        {c.displayName ? (
                            <span
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    padding: '2px 8px',
                                    borderRadius: 999,
                                    fontSize: 12,
                                    fontWeight: 600,
                                    color: 'var(--brand-as-neutral-700, #4b5563)',
                                    background: 'var(--brand-as-neutral-black-transparent-05)'
                                }}
                                title={c.displayName}
                            >
                                {c.displayName}
                            </span>
                        ) : null}
                        <span>{renderCell(c.canvassId)}</span>
                    </span>
                </td>
                <td>{c.timestamp ? new Date(c.timestamp).toLocaleDateString() : '—'}</td>
                <td className={styles.statusCell}>{renderStatus(c.status)}</td>
                <td className="text-end" style={{ width: 60 }}>
                    {c.canvassId ? (
                            <Icon
                                name="user_actions/more_horiz"
                                color="var(--brand-as-neutral-950)"
                                height="20"
                                width="20"
                                style={{ cursor: 'pointer' }}
                                title="View Canvass"
                                onClick={(e) => { e.stopPropagation(); openCanvassModal(c); }}
                            />
                    ) : null}
                </td>
            </tr>
        ));
    };

    const renderMobileCards = () => {
        if (!Array.isArray(canvasses) || canvasses.length === 0) {
            return (
                <div className={styles.mobileEmptyState}>No canvasses found</div>
            );
        }

        return canvasses.map((c, idx) => (
            <div
                key={c.id || c.canvassId || idx}
                className={styles.mobileCanvassCard}
                role={c.canvassId ? 'button' : undefined}
                tabIndex={c.canvassId ? 0 : undefined}
                onClick={() => {
                    if (c.canvassId) {
                        navigate(`/canvass/${encodeURIComponent(c.canvassId)}`);
                    }
                }}
                onKeyDown={(e) => {
                    if (c.canvassId && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault();
                        navigate(`/canvass/${encodeURIComponent(c.canvassId)}`);
                    }
                }}
            >
                <div className={styles.mobileCardHeader}>
                    <div className={styles.mobileCardTitleBlock}>
                        <div className={styles.mobileCardTitle}>{c.canvassName || c.canvassId || 'Untitled'}</div>
                        <div className={styles.mobileCardMetaRow}>
                            {c.displayName ? (
                                <span className={styles.mobileClientBadge} title={c.displayName}>{c.displayName}</span>
                            ) : null}
                            <span className={styles.mobileCanvassId}>{renderCell(c.canvassId)}</span>
                        </div>
                    </div>
                    <div className={styles.mobileStatusWrap}>{renderStatus(c.status)}</div>
                </div>

                <div className={styles.mobileCardDetails}>
                    <div className={styles.mobileDetailItem}>
                        <span className={styles.mobileDetailLabel}>Deployed</span>
                        <span className={styles.mobileDetailValue}>{c.timestamp ? new Date(c.timestamp).toLocaleDateString() : '—'}</span>
                    </div>
                    <div className={styles.mobileDetailItem}>
                        <span className={styles.mobileDetailLabel}>Client</span>
                        <span className={styles.mobileDetailValue}>{c.displayName || '—'}</span>
                    </div>
                </div>

                <div className={styles.mobileCardActions}>
                    <button
                        type="button"
                        className={styles.mobileActionButton}
                        onClick={(e) => {
                            e.stopPropagation();
                            if (c.canvassId) {
                                navigate(`/canvass/${encodeURIComponent(c.canvassId)}`);
                            }
                        }}
                    >
                        Open
                    </button>
                    {c.canvassId ? (
                        <button
                            type="button"
                            className={styles.mobileActionButtonSecondary}
                            onClick={(e) => {
                                e.stopPropagation();
                                setSelectedCanvass(c);
                                setShowModal(true);
                                setThreadLoading(true);
                                setThreadError('');
                                setThreadMessages([]);
                                fetchCanvassOnboardingInterview({ canvassId: c.canvassId })
                                    .then((result) => {
                                        const doc = Array.isArray(result) ? result[0] : result;
                                        const msgs = doc?.messages || [];
                                        setThreadMessages(msgs);
                                    })
                                    .catch((e) => {
                                        setThreadError(e?.message || 'Failed to load conversation thread');
                                    })
                                    .finally(() => {
                                        setThreadLoading(false);
                                    });
                            }}
                        >
                            Details
                        </button>
                    ) : null}
                </div>
            </div>
        ));
    };

    return (
        <Container className={styles.dataContainer}>
            {/* Error Alert */}
            {error && (
                <AugAlert type="error" title="Error Alert" text={error} className="mb-4" />
            )}
            <div className={styles.canvassesCard}>
                {loading ? 'Loading canvasses…' : `${canvassesCount} Canvasses`}
            </div>
            <div className="chat-filters">
                <Row>
                    <Col md={4}>
                        <Form.Group>
                            <SearchBar
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={2} className="ms-auto d-flex justify-content-end">
                        <Form.Group>
                            <Select
                                value={selectedStatus}
                                onChange={(e) => setSelectedStatus(e.target.value)}
                                options={[
                                    { value: '', label: 'Show All' },
                                    { value: 'draft', label: 'Draft' },
                                    { value: 'approved', label: 'Pending Activation' },
                                    { value: 'active', label: 'Live' },
                                    { value: 'complete', label: 'Complete' },
                                ]}
                            />
                        </Form.Group>
                    </Col>
                    <Col md={2} className="d-flex justify-content-end">
                        <Form.Group>
                            <Select
                                value={pageSize}
                                onChange={(e) => setPageSize(e.target.value)}
                                options={[
                                    { value: '10', label: '10 Per Page' },
                                    { value: '20', label: '20 Per Page' },
                                    { value: '30', label: '30 per Page' }
                                ]}
                            />
                        </Form.Group>
                    </Col>
                </Row>
            </div>
            <div className={styles.desktopTableWrap}>
                <SimpleTable 
                    className="canvasses-table"
                    headers={["Canvass Name", "Client | Canvass ID", "Deployed", "Status", ""]}
                    pagination={{
                        page,
                        totalPages: undefined, // unknown without count
                        hasNext,
                        loading,
                        onPageChange,
                        onPrev,
                        onNext
                    }}
                >
                    {renderRows()}
                </SimpleTable>
            </div>

            <div className={styles.mobileCardsWrap}>
                {renderMobileCards()}
            </div>

            <div className={styles.mobilePaginationWrap}>
                <div className={styles.mobilePaginationInfo}>
                    Page {page}
                </div>
                <div className={styles.mobilePaginationActions}>
                    <button
                        type="button"
                        className={styles.mobilePaginationButton}
                        onClick={onPrev}
                        disabled={loading || page <= 1}
                    >
                        Previous
                    </button>
                    <button
                        type="button"
                        className={styles.mobilePaginationButton}
                        onClick={onNext}
                        disabled={loading || !hasNext}
                    >
                        Next
                    </button>
                </div>
            </div>

            {showModal && selectedCanvass && (
                <Modal
                    open={showModal}
                    onClose={() => { setShowModal(false); setThreadMessages([]); setThreadError(''); setAutomationLoading(false); setDuplicationLoading(false); setDeletionLoading(false); setAutomationError(''); setDuplicationError(''); setDeletionError(''); setShowAutomationConfirm(false); setShowDeleteConfirm(false); setAutomationInterviewCount(1); setTimeout(() => setSelectedCanvass(null), 200); }}
                    title="Canvass Details"
                    actions={
                        <>
                            <Button 
                                variant="outline-primary" 
                                size="sm"
                                className="download-conversation-btn me-2"
                                onClick={() => exportCanvassConversationToPDF(false)}
                                title="Download onboarding interview as PDF"
                            >
                                <i className="bi bi-download"></i> PDF
                            </Button>
                            <Button 
                                variant="outline-primary" 
                                size="sm"
                                className="download-conversation-btn me-2"
                                onClick={() => exportCanvassConversationToPDF(true)}
                                title="Download onboarding interview as PDF"
                            >
                                <i className="bi bi-download"></i> PDF with messages
                            </Button>
                        </>
                    }
                >
                    <div className={styles.canvassModalContent}>
                    <div className={`mb-3 ${styles.canvassModalHeader}`}>
                        <p className="mb-0">
                            {(selectedCanvass.canvassName || 'Untitled')} — {selectedCanvass.canvassId}
                        </p>
                    </div>

                    <div className={`${styles.canvassModalBody} side-panel-body`}>
                        <div className={`${styles.canvassModalSection} user-info-section`}>
                            <h6>Canvass Information</h6>
                            <div className={`${styles.canvassInfoGrid} info-grid`}>
                                <div className={`${styles.canvassInfoItem} info-item`}>
                                    <span className="info-label">Canvass ID:</span>
                                    <span className={`${styles.canvassInfoValue} info-value`}>{selectedCanvass.canvassId}</span>
                                </div>
                                <div className={`${styles.canvassInfoItem} info-item`}>
                                    <span className="info-label">Name:</span>
                                    <span className={`${styles.canvassInfoValue} info-value`}>{selectedCanvass.canvassName || 'Untitled'}</span>
                                </div>
                                <div className={`${styles.canvassInfoItem} info-item`}>
                                    <span className="info-label">Client:</span>
                                    <span className={`${styles.canvassInfoValue} info-value`}>{renderCell(selectedCanvass.displayName) || renderCell(selectedCanvass.ownerId) || renderCell(selectedCanvass.audience) || '—'}</span>
                                </div>
                                <div className={`${styles.canvassInfoItem} info-item`}>
                                    <span className="info-label">Deployed:</span>
                                    <span className={`${styles.canvassInfoValue} info-value`}>{selectedCanvass.timestamp ? new Date(selectedCanvass.timestamp).toLocaleString() : '—'}</span>
                                </div>
                                <div className={`${styles.canvassInfoItem} info-item`}>
                                    <span className="info-label">Environment:</span>
                                    <span className={`${styles.canvassInfoValue} info-value`}>{renderCell(selectedCanvass.environment) || '—'}</span>
                                </div>
                            </div>
                        </div>

                        <div className={`${styles.canvassModalSection} ${styles.canvassMessagesSection} messages-section`}>
                            <h6>Conversation Thread</h6>
                            {threadLoading && (
                                <p className="text-muted">Loading conversation…</p>
                            )}
                            {threadError && (
                                <p className="text-danger small">{threadError}</p>
                            )}
                            {!threadLoading && !threadError && (
                                <div className={`${styles.canvassThreadContainer} conversation-thread-container`}>
                                    {Array.isArray(threadMessages) && threadMessages.length > 0 ? (
                                        threadMessages.map((message, idx) => (
                                            <div key={idx} className={`${styles.canvassMessageWrapper} message-wrapper ${message.role}`}>
                                                <div className={`${styles.canvassMessageContent} message-content`}>
                                                    <div className={`${styles.canvassMessageBubble} message-bubble ${message.role}`}>
                                                        {(() => {
                                                            if (typeof message.content === 'string') {
                                                                return message.content;
                                                            } else if (message?.content && typeof message.content === 'object') {
                                                                // Introduction text
                                                                if (message.content.introduction_text) {
                                                                    return (
                                                                        <div className="interview-intro-message">
                                                                            {message.content.introduction_text}
                                                                        </div>
                                                                    );
                                                                }
                                                                // Next question
                                                                if (message.content.next_question) {
                                                                    return (
                                                                        <div className="interview-next-question">
                                                                            <strong>{message.content.next_question}</strong>
                                                                        </div>
                                                                    );
                                                                }
                                                                // Fallback structured JSON
                                                                return <pre style={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify(message.content, null, 2)}</pre>;
                                                            }
                                                            return null;
                                                        })()}
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <p className="text-muted">No conversation messages available.</p>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className={`${styles.canvassModalSection} ${styles.canvassActionsSection} conversation-stats-section`}>
                            <h6>Actions</h6>
                            {automationError && (
                                <div className="mt-2">
                                    <AugAlert type="error" title="Automation Error" text={automationError} />
                                </div>
                            )}

                            {duplicationError && (
                                <div className="mt-2">
                                    <AugAlert type="error" title="Duplication Error" text={duplicationError} />
                                </div>
                            )}

                            {deletionError && (
                                <div className="mt-2">
                                    <AugAlert type="error" title="Deletion Error" text={deletionError} />
                                </div>
                            )}
                            <div className={`${styles.canvassActionPanel} interview-link-section`} style={{ padding: '15px', borderBottom: '1px solid #e1e5eb' }}>
                                {isAdmin && (
                                    <AugButton 
                                        onClick={() => navigate(`/canvasses/${encodeURIComponent(selectedCanvass.canvassId)}/interviews`)}
                                        style={{ 
                                            width: '100%',
                                            background: 'white',
                                            border: '2px solid #000',
                                            color: 'black',
                                            padding: '12px 24px',
                                            fontSize: '16px',
                                            fontWeight: '500',
                                            borderRadius: '8px',
                                            transition: 'all 0.3s ease'
                                        }}
                                        onMouseEnter={(e) => { e.target.style.background = '#f8f9fa'; }}
                                        onMouseLeave={(e) => { e.target.style.background = 'white'; }}
                                    >
                                        <i className="bi bi-box-arrow-up-right me-2"></i>
                                        View Interview Responses
                                    </AugButton>
                                )}

                                {String(selectedCanvass.status || '').toLowerCase() === 'active' && (
                                    <AugButton
                                        onClick={() => {
                                            setAutomationError('');
                                            setAutomationInterviewCount(1);
                                            setShowAutomationConfirm(true);
                                        }}
                                        disabled={automationLoading}
                                        style={{
                                            width: '100%',
                                            marginTop: '10px',
                                            background: automationLoading ? '#f8f9fa' : 'black',
                                            border: '2px solid #000',
                                            color: automationLoading ? '#6c757d' : 'white',
                                            padding: '12px 24px',
                                            fontSize: '16px',
                                            fontWeight: '500',
                                            borderRadius: '8px',
                                            transition: 'all 0.3s ease'
                                        }}
                                    >
                                        <i className="bi bi-play-fill me-2"></i>
                                        {automationLoading ? 'Started Automation...' : 'Start Automation'}
                                    </AugButton>
                                )}

                                {showAutomationConfirm && (
                                    <Modal
                                        open={showAutomationConfirm}
                                        onClose={() => { if (!automationLoading) { setShowAutomationConfirm(false); } }}
                                        title="Start Automation"
                                        actions={
                                            <>
                                                <AugButton
                                                    variant="secondary"
                                                    onClick={() => { setShowAutomationConfirm(false); }}
                                                    disabled={automationLoading}
                                                >
                                                    Cancel
                                                </AugButton>
                                                <AugButton
                                                    variant="primary"
                                                    onClick={() => {
                                                        const count = Math.max(1, parseInt(String(automationInterviewCount), 10) || 1);
                                                        setAutomationLoading(true);
                                                        setAutomationError('');
                                                        setShowAutomationConfirm(false);
                                                        void startAutomation({ canvassId: selectedCanvass.canvassId, numberOfInterviews: count, customPromptInjection: customPromptInjection.trim() })
                                                            .catch(() => {
                                                                setAutomationError('Failed to start automation');
                                                            })
                                                            .finally(() => {
                                                                setAutomationLoading(false);
                                                            });
                                                    }}
                                                    disabled={automationLoading}
                                                >
                                                    {automationLoading ? 'Starting...' : 'Start'}
                                                </AugButton>
                                            </>
                                        }
                                    >
                                        <div className="mb-3">
                                            <div className="text-muted small mb-2">Select how many interviews to run</div>
                                            <Form.Group>
                                                <Form.Control
                                                    type="number"
                                                    min={1}
                                                    step={1}
                                                    value={automationInterviewCount}
                                                    onChange={(e) => setAutomationInterviewCount(e.target.value)}
                                                    disabled={automationLoading}
                                                />
                                            </Form.Group>
                                        </div>
                                        <div className="mb-3">
                                            <div className="text-muted small mb-2">Respondent Personality Profile (optional - max 500 characters)</div>
                                            <Form.Group>
                                                <Form.Control
                                                    as="textarea"
                                                    rows={3}
                                                    maxLength={500}
                                                    value={customPromptInjection}
                                                    onChange={(e) => setCustomPromptInjection(e.target.value)}
                                                    disabled={automationLoading}
                                                    placeholder="Describe the respondent's background, experience, communication style, and perspective. Example: 'You are a 35-year-old marketing manager named Sarah who has worked in tech startups for 10 years. You're passionate about brand building and give practical, experience-based answers.'"
                                                />
                                                <div className="text-muted small mt-1">
                                                    {customPromptInjection.length}/500 characters
                                                </div>
                                            </Form.Group>
                                        </div>
                                    </Modal>
                                )}

                                {showDeleteConfirm && (
                                    <Modal
                                        open={showDeleteConfirm}
                                        onClose={() => { if (!deletionLoading) { setShowDeleteConfirm(false); } }}
                                        title="Delete Canvass"
                                        actions={
                                            <>
                                                <AugButton
                                                    variant="secondary"
                                                    onClick={() => { setShowDeleteConfirm(false); }}
                                                    disabled={deletionLoading}
                                                    style={{
                                                        background: 'white',
                                                        border: '2px solid #000',
                                                        color: 'black',
                                                        padding: '12px 24px',
                                                        fontSize: '16px',
                                                        fontWeight: '500',
                                                        transition: 'all 0.3s ease'
                                                    }}
                                                >
                                                    Cancel
                                                </AugButton>
                                                <AugButton
                                                    variant="primary"
                                                    onClick={async () => {
                                                        setDeletionLoading(true);
                                                        setDeletionError('');
                                                        setShowDeleteConfirm(false);
                                                        try {
                                                            await deleteCanvass(selectedCanvass.canvassId, { returnFull: true });
                                                            setShowModal(false);
                                                            setTimeout(() => setSelectedCanvass(null), 200);
                                                            // Remove deleted canvass from local state
                                                            setCanvasses(prev => prev.filter(canvass => canvass.canvassId !== selectedCanvass.canvassId));
                                                            setCanvassesCount(prev => prev - 1);
                                                        } catch (e) {
                                                            setDeletionError('Failed to delete canvass');
                                                        } finally {
                                                            setDeletionLoading(false);
                                                        }
                                                    }}
                                                    style={{
                                                        background: 'var(--brand-as-purple-800)',
                                                        border: '2px solid var(--brand-as-purple-800)',
                                                        color: 'white',transition: 'all 0.3s ease'
                                                    }}
                                                    disabled={deletionLoading}
                                                >
                                                    {deletionLoading ? 'Deleting...' : 'Delete'}
                                                </AugButton>
                                            </>
                                        }
                                    >
                                        <p className="mb-3">
                                            <div className="text-muted small">
                                                This will mark the canvass as deleted. This action cannot be undone.
                                            </div>
                                        </p>          
                                        <p className="mb-3 text-muted">
                                            Canvass ID: {selectedCanvass.canvassId || '—'}
                                            <br />
                                            Canvass Name: {selectedCanvass.canvassName || '—'}
                                            <br />
                                            Status: {selectedCanvass?.status || '—'}
                                            <br />
                                            Objective: {selectedCanvass?.objective || '—'}
                                            <br />
                                            Description: {selectedCanvass?.canvassDescription || '—'}
                                        </p>
                                    </Modal>
                                )}

                                <AugButton
                                    variant="secondary"
                                    onClick={async () => {
                                        setDuplicationLoading(true);
                                        setDuplicationError('');
                                        try {
                                            const res = await duplicateCanvass(selectedCanvass.canvassId, { returnFull: true });
                                            const newCanvassId = res?.canvass?.id;
                                            if (newCanvassId) {
                                                navigate(`/canvass/${encodeURIComponent(newCanvassId)}`);
                                            } else {
                                                setDuplicationError('Failed to duplicate canvass');
                                            }
                                        } catch (e) {
                                            setDuplicationError('Failed to duplicate canvass');
                                        } finally {
                                            setDuplicationLoading(false);
                                        }
                                    }}
                                    style={{
                                        width: '100%',
                                        marginTop: '10px',
                                        background: 'var(--brand-as-green-600)',
                                        border: '2px solid var(--brand-as-green-600)',
                                        color: 'white',
                                        padding: '12px 24px',
                                        fontSize: '16px',
                                        fontWeight: '500',
                                        borderRadius: '8px',
                                        transition: 'all 0.3s ease'
                                    }}
                                >
                                    <i className="bi bi-files me-2"></i>
                                    {duplicationLoading ? 'Duplicating...' : 'Duplicate Canvass'}
                                </AugButton>
                                <AugButton
                                    variant="secondary"
                                    onClick={() => {
                                        setDeletionError('');
                                        setShowDeleteConfirm(true);
                                    }}
                                    disabled={deletionLoading}
                                    style={{
                                        width: '100%',
                                        marginTop: '10px',
                                        background: 'var(--brand-as-purple-800)',
                                        border: '2px solid var(--brand-as-purple-800)',
                                        color: 'white',
                                        padding: '12px 24px',
                                        fontSize: '16px',
                                        fontWeight: '500',
                                        borderRadius: '8px',
                                        transition: 'all 0.3s ease'
                                    }}
                                >
                                    <i className="bi bi-trash me-2"></i>
                                    {deletionLoading ? 'Deleting...' : 'Delete Canvass'}
                                </AugButton>
                            </div>
                        </div>
                    </div>
                    </div>
                </Modal>
            )}
        </Container>
    );
};

export default CanvassesTable;
